<template>
  <div class="gmap-zone">
    <div class="gmap-zone__box">
      <header class="gmap-zone__box__header">
        <ui-button
          class="gmap-zone__box__header__close"
          @click.prevent="setCurrentZone(null)"
          button="secondary"
          :label="$t('commons.buttons.close')"
          :icon-only="true"
          icon="close"
        />
        <div class="gmap-zone__box__header__marker">
          <marker-default class="gmap-zone__box__header__marker__img" :filters="filters" :current-value="zone.value" />
        </div>
        <div class="gmap-zone__box__header__title">
          {{ zone.name[$i18n.locale] || zone.name.default }}
        </div>
      </header>
      <div class="gmap-zone__box__container">
        <div class="gmap-zone__box__container__section">
          <div class="gmap-zone__box__container__section__title">
            {{ $t('zone.title') }}
          </div>
          <div class="gmap-zone__box__container__section__content">
            <div class="gmap-zone__box__container__section__content__detail" v-if="zone.value">
              <div class="gmap-zone__box__container__section__content__detail__icon backoffice-icons">arrow_right</div>
              <div class="gmap-zone__box__container__section__content__detail__label">
                {{ zone.labels.value[$i18n.locale] || zone.labels.value.default }}
              </div>
              <div class="gmap-zone__box__container__section__content__detail__value">
                {{ zone.value }}
              </div>
            </div>
            <div class="gmap-zone__box__container__section__content__detail" v-if="zone.location_count">
              <div class="gmap-zone__box__container__section__content__detail__icon backoffice-icons">arrow_right</div>
              <div class="gmap-zone__box__container__section__content__detail__label">
                {{ zone.labels.location_count[$i18n.locale] || zone.labels.location_count.default }}
              </div>
              <div class="gmap-zone__box__container__section__content__detail__value">
                {{ zone.location_count }}
              </div>
            </div>
            <div class="gmap-zone__box__container__section__content__detail" v-if="zone.count">
              <div class="gmap-zone__box__container__section__content__detail__icon backoffice-icons">arrow_right</div>
              <div class="gmap-zone__box__container__section__content__detail__label">
                {{ zone.labels.count[$i18n.locale] || zone.labels.count.default }}
              </div>
              <div class="gmap-zone__box__container__section__content__detail__value">
                {{ zone.count }}
              </div>
            </div>
            <div class="gmap-zone__box__container__section__content__detail" v-if="zone.min_value">
              <div class="gmap-zone__box__container__section__content__detail__icon backoffice-icons">arrow_right</div>
              <div class="gmap-zone__box__container__section__content__detail__label">
                {{ zone.labels.min_value[$i18n.locale] || zone.labels.min_value.default }}
              </div>
              <div class="gmap-zone__box__container__section__content__detail__value">
                {{ zone.min_value }}
              </div>
            </div>
            <div class="gmap-zone__box__container__section__content__detail" v-if="zone.max_value">
              <div class="gmap-zone__box__container__section__content__detail__icon backoffice-icons">arrow_right</div>
              <div class="gmap-zone__box__container__section__content__detail__label">
                {{ zone.labels.max_value[$i18n.locale] || zone.labels.max_value.default }}
              </div>
              <div class="gmap-zone__box__container__section__content__detail__value">
                {{ zone.max_value }}
              </div>
            </div>
          </div>
        </div>
        <div class="gmap-zone__box__container__cta">
          <router-link
            tag="div"
            :to="{
              name: 'division',
              params: { ...$route.params, level: zone.level, key: zone.key },
              query: $route.query,
            }"
            v-slot="{ navigate }"
          >
            <ui-button
              class="gmap-zone__box__container__cta__button"
              button="cta"
              variant="gtr"
              :label="$t('zone.actions.division')"
              @click.native="onClickSetCurrentZone"
              @click="navigate"
            />
          </router-link>
        </div>
      </div>

      <footer class="gmap-zone__box__footer">
        <ui-button
          class="gmap-zone__box__footer__close"
          :label="$t('commons.buttons.close')"
          button="primary"
          @click.prevent="setCurrentZone(null)"
        />
      </footer>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import MarkerDefault from '@/components/Marker/Default.vue'
import UiButton from '@/components/UI/Button.vue'
import { launchGAEvent } from '@/utils/gtm.util'

export default {
  name: 'GmapZone',
  components: {
    UiButton,
    MarkerDefault,
  },
  props: {
    zone: {
      type: Object,
      required: true,
    },
    filters: {
      type: Array,
      required: true,
    },
  },
  methods: {
    ...mapActions({
      setCurrentZone: 'data/setCurrentZone',
    }),
    onClickSetCurrentZone() {
      launchGAEvent(this.$route.params.id, 'see_locations_in_area', 'show')
    },
  },
}
</script>

<style lang="scss" scoped>
.gmap-zone {
  @include wrapper(flex-start, $gutter-md 0 $gutter-md $gutter-md);

  @media (min-width: $screen-sm) {
    right: initial;
    bottom: initial;
    width: calc(#{$gmap-sidebar-width-tablet} + #{$gutter-md});
  }

  @media (min-width: $screen-lg) {
    width: calc(#{$gmap-sidebar-width-desktop} + #{$gutter-md});
  }

  &__box {
    @include box;

    width: $gmap-sidebar-width-mobile;

    @media (min-width: $screen-sm) {
      width: $gmap-sidebar-width-tablet;
    }

    @media (min-width: $screen-lg) {
      width: $gmap-sidebar-width-desktop;
    }

    &__header {
      @include header;

      &__close {
        @include close('top');
      }

      &__marker {
        margin-right: 12px;
        width: 20px;
      }

      &__title {
        @include title(1rem);

        display: -webkit-box;
        flex: 1;
        padding-bottom: 1px;
        overflow: hidden;
        text-overflow: ellipsis;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
      }
    }

    &__container {
      @include container;

      &__section {
        &__title {
          @include sub-title;
        }

        &__content {
          &__detail {
            display: flex;
            align-items: center;
            margin-bottom: 5px;

            &:last-child {
              margin-bottom: 0;
            }

            &__icon {
              margin-right: 12px;
            }

            &__label {
              margin-right: 12px;
            }

            &__value {
              font-weight: bold;
            }
          }
        }
      }

      &__cta {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: $spacing-sm;

        @media (min-width: $screen-sm) {
          margin-top: $spacing-md;
        }
      }
    }

    &__footer {
      @include footer;

      @media (min-width: $screen-sm) {
        display: none;
      }
    }
  }
}
</style>
